// Reader-facing copy for a gloss generation that did not land.
//
// `glossSelection`'s input guard throws `ConvexError({ code })` with no
// `message`, and the generic `errorMessage` flattener falls back to the code —
// so without this a reader saw `quote_too_long` in red under a "Try again"
// button that could never succeed. Every code the gloss path can throw is
// mapped here, and each answer says whether pressing the button again could
// change the outcome: the input codes are deterministic for a selection, so
// the only useful advice is to select something else.
//
// `unauthenticated` and `quota_exceeded` never reach this: the popover routes
// them to the sign-in and subscribe affordances first.

import { ConvexError } from "convex/values";
import {
  assertValidGlossInput,
  GLOSS_INPUT_ERROR,
} from "@bindery/convex-retells/model/glossInput";
import { RESOLVE_LIMIT_ERROR } from "@bindery/convex-retells/model/translationQuota";
import { retryPhrase } from "@/lib/quota-error";

export interface GlossFailure {
  /** The red line shown in place of the generate prompt. */
  message: string;
  /** Whether "Try again" is worth offering — false when the same selection
   *  would be refused the same way. */
  retryable: boolean;
}

/** Reader-facing copy for a generation that failed for no reason it named. */
export const GENERATE_FAILED = "That translation didn’t come through.";

const BY_CODE: Readonly<Record<string, GlossFailure>> = {
  [GLOSS_INPUT_ERROR.quoteTooLong]: {
    message:
      "That selection is too long to gloss. Select a word, a phrase or a single sentence instead.",
    retryable: false,
  },
  [GLOSS_INPUT_ERROR.paragraphTooLong]: {
    message:
      "That selection reaches across too much of the text to gloss. Select something within one paragraph instead.",
    retryable: false,
  },
  [GLOSS_INPUT_ERROR.notContained]: {
    message:
      "That selection couldn’t be matched to its passage. Select it again within one paragraph.",
    retryable: false,
  },
  [GLOSS_INPUT_ERROR.spanUnresolved]: {
    message:
      "That passage couldn’t be found in the book’s text. Reload the page and select it again.",
    retryable: false,
  },
  forbidden: {
    message: "This account can’t generate translations.",
    retryable: false,
  },
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * What to tell the reader about a rejected `glossSelection` call.
 *
 * A known code gets its own copy; a `ConvexError` carrying a `message` the
 * server wrote for readers shows that; anything else — an unknown code, or a
 * plain `Error` whose text is Convex's opaque "[CONVEX A(...)] Server Error" —
 * gets the generic line and a retry, since a transient upstream failure is
 * the likeliest cause. A lookup refused for its rate says when it lifts,
 * from the `retryAt` it carries, in place of a retry that would be refused.
 */
export function describeGlossFailure(
  error: unknown,
  now = Date.now(),
): GlossFailure {
  if (error instanceof ConvexError && isRecord(error.data)) {
    const { code, message, retryAt } = error.data;
    if (code === RESOLVE_LIMIT_ERROR) {
      const when =
        typeof retryAt === "number" ? retryPhrase(retryAt, now) : "in a while";
      return {
        message: `Too many lookups in a row. Try again ${when}.`,
        retryable: false,
      };
    }
    if (typeof code === "string") {
      const known = BY_CODE[code];
      if (known !== undefined) return known;
    }
    if (typeof message === "string" && message !== "") {
      return { message, retryable: true };
    }
  }
  return { message: GENERATE_FAILED, retryable: true };
}

/**
 * The refusal the server's input guard would return for this selection, or
 * `null` when it would pass. Run before offering generation so a selection
 * the server is bound to refuse never spends a round trip on the attempt —
 * the same guard, imported rather than restated, so the two cannot drift.
 */
export function glossInputFailure(
  quote: string,
  paragraph: string,
): GlossFailure | null {
  try {
    assertValidGlossInput(quote, paragraph);
    return null;
  } catch (error) {
    return describeGlossFailure(error);
  }
}
